import React, { useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { CheckboxLabel } from '../checkbox/Checkbox';
import useDidMountEffect from '../../../hooks/useDidMountEffect';
import ensureInView from '../../utils/ensureInView';
import { mainTheme } from '@plecto/ui';

const animationDuration = 200;

const StyledItem = styled.div`
	cursor: pointer;
	color: ${mainTheme.form.dropdown.menu.color};
	background-color: ${mainTheme.form.dropdown.menu.default};
	padding: 8px 12px;
	font-size: 14px;
	transition: background-color ${animationDuration}ms, color ${animationDuration}ms;
	margin: 0;

	&:hover { 
		background-color: ${mainTheme.form.dropdown.menu.hover.background};
	}

	& ${CheckboxLabel} {
		color: ${mainTheme.form.dropdown.menu.color};
	}

	// menu.selected is the selected row's text colour, not a fill: painted as the background it
	// turned the row near-white. The row brightens its label and takes the hover wash instead.
	&.dropdown-menu-item-selected {
		color: ${mainTheme.form.dropdown.menu.selected};
		background-color: ${mainTheme.form.dropdown.menu.hover.background};

		${props => props.hasChildren ? css`
			padding-top: 4px;
			padding-bottom: 4px;
		` : ''}
	}

	& .react-select__menu .react-select__option {
		cursor: pointer;
	}
`;

const StyledItemOverlay = styled.div`
	${props => props.inline ? css`
		display: flex;
		flex-wrap: wrap;
		align-items: center;

		& > * {
			margin-right: 0.5em;
		}

		& .react-select-container {
			.react-select__control {
				min-height: 28px;
				max-height: 28px;
				height: 28px;
				font-size: 14px;

				.react-select__value-container {
					padding-top: 0;
					padding-bottom: 0;
				}

				.react-select__indicator {
					padding: 4px;
				}
			}
		}
	` : ''}
`;

export default function DropDownMenuItem({ label, value, selectValue, selected, onSelect, children, inline }) {
	const onClick = useCallback(() => {
		if (!selected) {
			onSelect(value, selectValue);
		}
	},[onSelect, selectValue, selected, value]);

	const itemRef = useRef();

	useDidMountEffect(() => {
		if (selected) {
			// do not use scrollIntoView here, as it causes page scrolling on reports
			ensureInView(itemRef.current.parentNode, itemRef.current);
		}
	});

	return (
		<StyledItem
			ref={itemRef}
			className={classNames({ 'dropdown-menu-item-selected': selected })}
			onClick={onClick}
			hasChildren={Boolean(children)}
		>
			<StyledItemOverlay selected={selected} inline={inline}>
				<span>
					{label}
				</span>
				{selected ? children : null}
			</StyledItemOverlay>
		</StyledItem>
	);
}
